import { Link } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { PageIntro } from "@/components/site/PageIntro";
import { PriceTag } from "@/components/site/ServiceCard";
import { FaqList, faqJsonLd } from "@/components/site/FaqList";
import { Loading, ErrorState } from "@/components/site/Primitives";
import { gstLine } from "@/lib/format";

const offerLd = (site, services) => ({
  "@context": "https://schema.org", "@type": "OfferCatalog", name: `${site?.brand?.name || "EditBay"} editing services`,
  itemListElement: services.filter((s) => !s.custom_quote && s.price_inr != null).map((s) => ({
    "@type": "Offer", name: s.name, priceCurrency: "INR",
    priceSpecification: { "@type": "UnitPriceSpecification", minPrice: s.price_inr, priceCurrency: "INR" },
  })),
});

export default function Pricing() {
  const { site, services, faqs, loading, error, reload } = useSite();
  useSeo("pricing", "/pricing", {}, site ? [offerLd(site, services), faqJsonLd(faqs)] : null);
  const pricing = site?.pricing || {};
  return (
    <div data-testid="pricing-page">
      <PageIntro eyebrow="Pricing" title="Know where you start." body={pricing.scope_note}>
        {pricing.show_promo && pricing.promo_text && <p className="mt-6 inline-block border border-[#0055FF] text-[#00E5FF] px-4 py-2 text-sm" data-testid="pricing-page-promo">{pricing.promo_text}</p>}
      </PageIntro>
      <div className="eb-container py-16">
        {loading && <Loading />}
        {error && <ErrorState message={error} onRetry={reload} />}
        <div className="grid md:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
          {services.map((s) => (
            <div key={s.id} className={`bg-[#050505] p-8 md:p-10 flex flex-col ${s.featured && !s.custom_quote ? "md:bg-[#0a0f1f]" : ""}`} data-testid={`pricing-tier-${s.slug}`}>
              <p className="eb-label">{s.category}</p>
              <h2 className="mt-3 text-2xl md:text-3xl font-bold">{s.name}</h2>
              <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{s.summary}</p>
              <div className="mt-8"><PriceTag service={s} large={!s.custom_quote} /></div>
              <p className="mt-6 text-sm text-zinc-500">{s.turnaround_text}</p>
              <div className="mt-auto pt-10">
                {s.available ? (
                  <Link to={`/enquire?service=${s.slug}`} className={s.custom_quote ? "eb-btn-ghost w-full" : "eb-btn-primary w-full"} data-testid={`pricing-cta-${s.slug}`}>
                    {s.custom_quote ? "Request a quote" : "Submit a brief"}
                  </Link>
                ) : <p className="text-amber-400 text-sm">Temporarily unavailable</p>}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-zinc-500" data-testid="pricing-page-gst">{gstLine(pricing)}</p>
        {site?.turnaround && (
          <div className="mt-16 grid md:grid-cols-12 gap-6 border-t border-[#27272A] pt-10">
            <p className="md:col-span-5 font-display text-2xl font-bold">{site.turnaround.headline}</p>
            <p className="md:col-span-7 text-zinc-400 leading-relaxed">{site.turnaround.note}</p>
          </div>
        )}
        {faqs.length > 0 && <div className="mt-20"><h2 className="text-3xl font-bold mb-8">Pricing questions</h2><FaqList faqs={faqs} /></div>}
      </div>
    </div>
  );
}
